import React, { useEffect, useRef, useState } from 'react';
import {
  Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Typography, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Paper, IconButton, Chip, Checkbox, Snackbar, Alert,
} from '@mui/material';
import { Add, Edit, Upload, Delete, Download } from '@mui/icons-material';
import api from '../../api/client';

interface DispositionRecord {
  id: number; code: string; name: string; sort_order: number; is_active: boolean;
}

export default function DispositionsPage() {
  const [dispositions, setDispositions] = useState<DispositionRecord[]>([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<DispositionRecord | null>(null);
  const [form, setForm] = useState({ code: '', name: '', sort_order: 0 });
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [snack, setSnack] = useState('');
  const fileRef = useRef<HTMLInputElement>(null);

  const load = () => { api.get('/dispositions/all').then((r) => { setDispositions(r.data); setSelected(new Set()); }).catch(() => api.get('/dispositions').then((r) => { setDispositions(r.data); setSelected(new Set()); })); };
  useEffect(() => { load(); }, []);

  const handleOpen = (d?: DispositionRecord) => {
    if (d) {
      setEditing(d);
      setForm({ code: d.code, name: d.name, sort_order: d.sort_order });
    } else {
      setEditing(null);
      setForm({ code: '', name: '', sort_order: dispositions.length + 1 });
    }
    setOpen(true);
  };

  const handleSave = async () => {
    if (editing) {
      await api.patch(`/dispositions/${editing.id}`, form);
    } else {
      await api.post('/dispositions', form);
    }
    setOpen(false);
    load();
  };

  const handleImport = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const r = await api.post('/dispositions/import-csv', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setSnack(`Imported: ${r.data.created} created, ${r.data.skipped} skipped`);
      load();
    } catch { setSnack('Import failed'); }
    if (fileRef.current) fileRef.current.value = '';
  };

  const handleBulkDelete = async () => {
    if (!selected.size || !window.confirm(`Delete ${selected.size} disposition(s)?`)) return;
    await api.post('/dispositions/bulk-delete', { ids: Array.from(selected) });
    setSnack(`Deleted ${selected.size} disposition(s)`);
    load();
  };

  const toggleSelect = (id: number) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };
  const toggleAll = () => {
    if (selected.size === dispositions.length) setSelected(new Set());
    else setSelected(new Set(dispositions.map((d) => d.id)));
  };

  const handleExport = () => {
    const headers = ['code', 'name', 'sort_order'];
    const rows = dispositions.map((d) => [
      d.code, d.name, d.sort_order,
    ].map((v) => `"${String(v).replace(/"/g, '""')}"`).join(','));
    const csv = [headers.join(','), ...rows].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'dispositions.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2, gap: 1, flexWrap: 'wrap' }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>Call Dispositions</Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          {selected.size > 0 && (
            <Button startIcon={<Delete />} color="error" variant="outlined" onClick={handleBulkDelete}>
              Delete ({selected.size})
            </Button>
          )}
          <input type="file" accept=".csv" ref={fileRef} hidden onChange={handleImport} />
          <Button startIcon={<Download />} variant="outlined" onClick={handleExport}>Export CSV</Button>
          <Button startIcon={<Upload />} variant="outlined" onClick={() => fileRef.current?.click()}>Import CSV</Button>
          <Button startIcon={<Add />} variant="contained" onClick={() => handleOpen()}>Add Disposition</Button>
        </Box>
      </Box>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell padding="checkbox"><Checkbox checked={dispositions.length > 0 && selected.size === dispositions.length} indeterminate={selected.size > 0 && selected.size < dispositions.length} onChange={toggleAll} /></TableCell>
              <TableCell>Code</TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Sort Order</TableCell>
              <TableCell>Status</TableCell>
              <TableCell width={60} />
            </TableRow>
          </TableHead>
          <TableBody>
            {dispositions.map((d) => (
              <TableRow key={d.id} selected={selected.has(d.id)}>
                <TableCell padding="checkbox"><Checkbox checked={selected.has(d.id)} onChange={() => toggleSelect(d.id)} /></TableCell>
                <TableCell sx={{ fontWeight: 700, fontFamily: 'monospace' }}>{d.code}</TableCell>
                <TableCell>{d.name}</TableCell>
                <TableCell>{d.sort_order}</TableCell>
                <TableCell><Chip size="small" label={d.is_active ? 'Active' : 'Inactive'} color={d.is_active ? 'success' : 'default'} /></TableCell>
                <TableCell><IconButton size="small" onClick={() => handleOpen(d)}><Edit fontSize="small" /></IconButton></TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editing ? 'Edit Disposition' : 'New Disposition'}</DialogTitle>
        <DialogContent>
          <TextField fullWidth label="Code" margin="normal" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="RPT" />
          <TextField fullWidth label="Name" margin="normal" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Report Taken" />
          <TextField fullWidth label="Sort Order" type="number" margin="normal" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSave}>Save</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!snack} autoHideDuration={4000} onClose={() => setSnack('')}>
        <Alert severity="info" onClose={() => setSnack('')}>{snack}</Alert>
      </Snackbar>
    </Box>
  );
}
